import * as Yup from 'yup';

import { AllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { assetAllocationSchema } from '../../shared-schemas';
import { createTotalWeightTest, createUniqueNameTest } from '../../shared-utils';

export const createModelFormSchema = (
    existingAllocationNames: string[] = [],
    existingStrategyNames: string[] = [],
    availableStrategies: Strategy[] = [],
    currentAllocationName?: string,
) =>
    Yup.object({
        allocationName: Yup.string()
            .required('Model name is required')
            .trim()
            .min(1, 'Model name cannot be empty')
            .max(100, 'Model name cannot exceed 100 characters')
            .test(
                'unique-name',
                'A model with this name already exists',
                createUniqueNameTest(existingAllocationNames, currentAllocationName),
            ),
        allocationType: Yup.string()
            .oneOf([AllocationType.PERSONAL, AllocationType.SHARED])
            .required('Model type is required'),
        strategyElements: Yup.array()
            .of(
                Yup.object({
                    mode: Yup.string().oneOf(['existing', 'new']).required(),
                    weight: Yup.string()
                        .required('Weight is required')
                        .test('is-positive', 'Weight must be greater than 0', function (value) {
                            const numValue = Number(value);
                            return !isNaN(numValue) && numValue > 0;
                        }),
                    existingStrategyGUID: Yup.string().when('mode', {
                        is: 'existing',
                        then: (schema) => schema.required('Select a strategy'),
                    }),
                    newStrategyName: Yup.string().when('mode', {
                        is: 'new',
                        then: (schema) =>
                            schema
                                .required('Strategy name is required')
                                .trim()
                                .min(1, 'Strategy name cannot be empty')
                                .test(
                                    'unique-name',
                                    'A strategy with this name already exists',
                                    createUniqueNameTest(existingStrategyNames),
                                ),
                    }),
                    newStrategyAssetAllocations: Yup.array()
                        .of(assetAllocationSchema)
                        .when('mode', {
                            is: 'new',
                            then: (schema) =>
                                schema
                                    .min(1, 'At least one asset allocation is required')
                                    .test('total-weight', 'Total weight must equal 100%', createTotalWeightTest()),
                        }),
                }),
            )
            .default([])
            .min(1, 'At least one strategy is required')
            .test('total-weight', 'Total weight must equal 100%', createTotalWeightTest())
            .test('unique-new-names', 'Strategy names must be unique', function (value) {
                if (!value) return true;
                const newNames = value
                    .filter((el) => el.mode === 'new' && el.newStrategyName)
                    .map((el) => el.newStrategyName!.trim().toLowerCase());
                return new Set(newNames).size === newNames.length;
            })
            .test('unique-existing-strategies', 'Each strategy can only be added once', function (value) {
                if (!value) return true;
                const guids = value
                    .filter((el) => el.mode === 'existing' && el.existingStrategyGUID)
                    .map((el) => el.existingStrategyGUID);
                return new Set(guids).size === guids.length;
            })
            .test(
                'no-personal-strategies-in-shared',
                'Shared models cannot include personal strategies',
                function (value) {
                    const allocationType = this.parent.allocationType;
                    if (allocationType !== AllocationType.SHARED || !value) return true;
                    return !value.some((el) => {
                        if (el.mode !== 'existing' || !el.existingStrategyGUID) return false;
                        const strategy = availableStrategies.find((s) => s.strategyGUID === el.existingStrategyGUID);
                        return strategy?.strategyType === StrategyType.PERSONAL;
                    });
                },
            ),
    });

export type ModelFormValues = Yup.InferType<ReturnType<typeof createModelFormSchema>>;
export type StrategyElementFormValue = ModelFormValues['strategyElements'][number];
